import AddIcon from "@mui/icons-material/Add"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  styled,
} from "@mui/material"
import type React from "react"
import { extraColors } from "../../utils/theme"
import { DefaultModelPicker } from "./ChatModelPicker"
import type { SessionInfo } from "./types"
import { dayjsUTC } from "../../utils/dates"

const SidebarContainer = styled(Box)(() => ({
  width: 280,
  height: "100%",
  backgroundColor: extraColors.dark,
  borderRight: `1px solid ${extraColors.translucentWhite}`,
  display: "flex",
  flexDirection: "column",
}))

const NewChatButton = styled(ListItemButton)(() => ({
  margin: "8px",
  borderRadius: "6px",
  border: `1px solid ${extraColors.translucentWhite}`,
  color: extraColors.white,
  "&:hover": {
    backgroundColor: extraColors.translucentWhite,
  },
}))

const WorkflowTag = styled(Chip)({
  height: 16,
  fontSize: 9,
  fontWeight: 700,
  textTransform: "uppercase",
  borderRadius: 2,
  background: "linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)",
  color: "#000",
  marginLeft: 8,
  "& .MuiChip-label": {
    paddingLeft: 4,
    paddingRight: 4,
  },
})

const ChatTitle = styled(Typography)(() => ({
  fontSize: 12,
  fontWeight: 600,
  color: extraColors.white,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
  width: "100%",
}))

const ChatTimestamp = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 400,
  color: extraColors.medium,
}))

interface ChatSidebarProps {
  sessions: SessionInfo[]
  activeSessionId: string | null
  streamingSessions?: Record<string, boolean>
  onSessionSelect: (id: string) => void
  onDeleteSession: (id: string) => void
  onNewChat: () => void
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  sessions,
  activeSessionId,
  streamingSessions = {},
  onSessionSelect,
  onDeleteSession,
  onNewChat,
}) => {
  return (
    <SidebarContainer>
      <Box sx={{ p: 1 }}>
        <NewChatButton onClick={onNewChat}>
          <AddIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary="New Chat" />
        </NewChatButton>
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <Typography
            variant="tiny"
            sx={{ color: extraColors.medium, mb: 0.75, display: "block" }}
          >
            Default Model
          </Typography>
          <DefaultModelPicker />
        </Box>
      </Box>

      <Divider sx={{ bgcolor: extraColors.translucentWhite }} />

      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <Typography
          variant="tiny"
          sx={{ px: 2, py: 1, color: extraColors.medium, display: "block" }}
        >
          Recent Chats
        </Typography>
        <List sx={{ px: 1 }}>
          {sessions.map(s => {
            const isWorkflow = s.title.startsWith("⚡")
            let displayTitle = s.title
            if (isWorkflow) {
              // @ts-expect-error - session.title is occasionally seen as undefined by tsc despite check
              displayTitle = s.title
                .replace("⚡ Workflow:", "")
                .split("[ID:")[0]
                .trim()
            }

            return (
              <ListItem
                key={s.id}
                disablePadding
                sx={{ mb: 0.5 }}
                secondaryAction={
                  streamingSessions[s.id] ? (
                    <CircularProgress
                      size={16}
                      thickness={4}
                      sx={{ color: extraColors.status.navyLight, mr: 1 }}
                    />
                  ) : (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={e => {
                        e.stopPropagation()
                        onDeleteSession(s.id)
                      }}
                      sx={{
                        color: extraColors.medium,
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <ListItemButton
                  selected={activeSessionId === s.id}
                  onClick={() => onSessionSelect(s.id)}
                  sx={{
                    borderRadius: "6px",
                    "&.Mui-selected": {
                      backgroundColor: extraColors.translucentWhite,
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  {isWorkflow ? (
                    <AutoAwesomeIcon
                      sx={{
                        mr: 1.5,
                        fontSize: 18,
                        color: "#4facfe",
                      }}
                    />
                  ) : (
                    <ChatBubbleOutlineIcon
                      sx={{
                        mr: 1.5,
                        fontSize: 18,
                        color: extraColors.status.navyLight,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <ChatTitle>{displayTitle}</ChatTitle>
                    {isWorkflow && <WorkflowTag label="Workflow" />}
                    <ChatTimestamp>
                      {dayjsUTC(s.created_at).format("MM/DD/YYYY HH:mm")}
                    </ChatTimestamp>
                  </Box>
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </SidebarContainer>
  )
}
